// PET-642 — rich-text copy payload for the Search "Ask a Question" chat
// thread. Kept out of SearchView.tsx so the payload shape is unit-testable
// without rendering the modal.
import type { CallRef } from "../lib/api";
import {
  buildCallRefMap,
  callIdsToHtml,
  callIdsToPlainText,
  escapeHtml,
} from "../lib/linkifyCallIds";

export type QaCopyTurn = {
  question: string;
  answer: string;
  callRefs: CallRef[];
};

// Structural subset of SearchView's ``SearchMode`` — only the fields the
// payload title needs, so SearchView can pass its mode straight through.
export type QaCopyMode =
  | { kind: "phone"; phone: string }
  | { kind: "agent"; agentName: string };

// Build a rich-text (HTML) + plain-text copy payload covering the whole
// Q&A thread. Each turn renders its question bold and the answer below
// it, with call IDs swapped for clickable ``<phone> (<n>)`` links.
//
// Correctness invariant: each turn's refMap is built from that turn's
// OWN ``callRefs`` — phone ordinals are scoped to the answer's analysed
// call pool, so the same call_id may legitimately carry different
// ordinals in different turns. Never collapse this into one thread-wide
// map.
export function buildQaCopyPayload(
  turns: QaCopyTurn[],
  mode: QaCopyMode,
  callCount: number,
): { html: string; text: string } {
  const subject = mode.kind === "phone" ? mode.phone : mode.agentName;
  const title = `Q&A about ${callCount} call${
    callCount === 1 ? "" : "s"
  } for ${subject}`;

  const htmlParts: string[] = [`<h2>${escapeHtml(title)}</h2>`];
  const textParts: string[] = [title, ""];

  for (const t of turns) {
    const refMap = buildCallRefMap(t.callRefs);
    htmlParts.push(`<p><strong>Q: ${escapeHtml(t.question)}</strong></p>`);
    htmlParts.push(
      `<p>${callIdsToHtml(t.answer, refMap).replace(/\n/g, "<br>")}</p>`,
    );
    textParts.push(`Q: ${t.question}`);
    textParts.push(callIdsToPlainText(t.answer, refMap));
    textParts.push("");
  }

  const html = `<!doctype html><html><body>${htmlParts.join("")}</body></html>`;
  const text =
    textParts.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
  return { html, text };
}
